import { z } from "zod";

// ---- live breaks: hosts open shoppers' boxes on stage, one queue entry at a time ---------------

/**
 * The timing contract every live break runs on (boxed's `LiveBreak.ts`). The server owns each
 * step; the storefront reads the same numbers to place its countdowns and stagger the reveal.
 */
/** How long a Presentation shows who bought what before the Reveal. */
export const LIVE_BREAK_PRESENT_MS = 6_000;
/** The spinner's beat: box *i* of an entry shows at `opener_delay_ms + i × beat`. */
export const LIVE_BREAK_REVEAL_BEAT_MS = 4_000;
/** The gap Auto Mode leaves on an idle stage before presenting the next entry. */
export const LIVE_BREAK_AUTO_GAP_MS = 5_000;
/** How long Auto Mode holds the results after the last box shows, before moving on. */
export const LIVE_BREAK_RESULTS_HOLD_MS = 10_000;
/** A host seen within this long is present; purchases need a present host. */
export const LIVE_BREAK_HOST_PRESENCE_MS = 300_000;
/** A host away this long has the server drive the queue (and overrides a pause). */
export const LIVE_BREAK_HOST_AWAY_TAKEOVER_MS = 900_000;
/** An idle, empty live break whose host has been away this long closes as abandoned. */
export const LIVE_BREAK_ABANDONED_CLOSE_MS = 1_800_000;
/** How long the directory keeps listing a live break after it closes. */
export const LIVE_BREAK_CLOSED_LISTING_MS = 300_000;
/** How often the tick stamps a house live break's presence. */
export const LIVE_BREAK_HOUSE_HEARTBEAT_MS = 60_000;
/** How long a dev stage's `idle_end` house live break may sit idle before it ends. */
export const LIVE_BREAK_IDLE_END_MS = 30_000;
/** The wait estimate's lead: added once before the entries ahead, and after each of them. */
export const LIVE_BREAK_ESTIMATE_BUFFER_MS = 10_000;
/** A viewer counts while their last heartbeat is within this long. */
export const LIVE_BREAK_VIEWER_WINDOW_MS = 120_000;

/** The largest host commission rate an environment may allow, in basis points (3%, the forge cut's cap). */
export const LIVE_BREAK_MAX_RATE_BPS = 300;
/** The rate a host starts at when the environment has not set one, in basis points (boxed's 1.5%). */
export const LIVE_BREAK_DEFAULT_RATE_BPS = 150;
/** The cheapest box a live break sells from outside its own boxes, by default, in US cents. */
export const LIVE_BREAK_DEFAULT_MIN_BOX_PRICE_CENTS = 500;
/** The dearest minimum box price an environment or a host may set, in US cents. */
export const LIVE_BREAK_MAX_MIN_BOX_PRICE_CENTS = 100_000_000;
/** Most boxes one queue entry buys. */
export const LIVE_BREAK_MAX_AMOUNT = 5;
/** Fewest and most boxes chosen for one live break. */
export const LIVE_BREAK_MIN_BOXES = 1;
export const LIVE_BREAK_MAX_BOXES = 50;
/** Longest live-break title, in characters. */
export const LIVE_BREAK_TITLE_MAX = 80;
/** Queued entries a live break holds by default, and the range staff may set. */
export const LIVE_BREAK_DEFAULT_QUEUE_CAP = 500;
export const LIVE_BREAK_MIN_QUEUE_CAP = 5;
export const LIVE_BREAK_MAX_QUEUE_CAP = 2_000;
/** The range of queued entries per buyer a host may set (null = no limit). */
export const LIVE_BREAK_MIN_BUYER_QUEUE_CAP = 1;
export const LIVE_BREAK_MAX_BUYER_QUEUE_CAP = 50;
/** The longest stage delay a host may set, to match their stream's latency. */
export const LIVE_BREAK_MAX_OPENER_DELAY_MS = 30_000;

const rateBpsSchema = z.int().min(0).max(LIVE_BREAK_MAX_RATE_BPS);
const minBoxPriceCentsSchema = z.int().min(0).max(LIVE_BREAK_MAX_MIN_BOX_PRICE_CENTS);

/**
 * Who goes live on a store (`hosting` in the live-break settings). `store`: the store's own hosts,
 * the accounts on its Host Allow List, one live break at a time, and the storefront's live-break
 * page is that one live break (or that the store isn't live). `community`: boxed's model, any
 * shopper the settings allow hosts their own, and the page is a directory of them.
 */
export const LIVE_BREAK_HOSTING_MODES = ["store", "community"] as const;
export type LiveBreakHostingMode = (typeof LIVE_BREAK_HOSTING_MODES)[number];

/**
 * An environment's live-break settings, as `storefront_configs.live_break_settings` stores them.
 * Every field defaults, so `{}` (the column's default) is the store's own live break (`hosting`
 * `store`), and otherwise boxed's: anyone may host once community live breaks are on, a 1.5%
 * default rate under a 3% maximum and a $5 minimum box price. Read leniently: a stored default
 * rate above the maximum is kept (every reader caps it, as boxed does). The box categories a live
 * break sells from are the storefront's own (`storefront_configs.box_categories`, Storefront →
 * Categories), not a setting here; an `excludedCategories` key an earlier save left is ignored.
 */
export const liveBreakSettingsSchema = z.object({
  /**
   * `store` (the default): only the Host Allow List hosts, one live break at a time, whatever
   * `hostAllowlistEnforced` says. `community`: `hostAllowlistEnforced` decides who may host.
   */
  hosting: z.enum(LIVE_BREAK_HOSTING_MODES).default("store"),
  /** True = only the Host Allow List may host; false = any signed-in shopper may (community only). */
  hostAllowlistEnforced: z.boolean().default(false),
  /** The rate a host without their own starts their live breaks at, in basis points. */
  defaultRateBps: rateBpsSchema.default(LIVE_BREAK_DEFAULT_RATE_BPS),
  /** The highest rate any host may charge, in basis points; 0 turns host commission off. */
  maxRateBps: rateBpsSchema.default(LIVE_BREAK_MAX_RATE_BPS),
  /** The minimum box price a new live break starts with, in US cents. */
  defaultMinBoxPriceCents: minBoxPriceCentsSchema.default(LIVE_BREAK_DEFAULT_MIN_BOX_PRICE_CENTS),
});
export type LiveBreakSettings = z.infer<typeof liveBreakSettingsSchema>;

/**
 * A save of the settings, written whole: the stored shape held to what a save may set, so a
 * default rate above the maximum is refused here (boxed's `setLiveBreakDefaultRate`) rather than
 * capped on read.
 */
export const liveBreakSettingsInputSchema = z
  .object({
    hosting: z.enum(LIVE_BREAK_HOSTING_MODES),
    hostAllowlistEnforced: z.boolean(),
    defaultRateBps: rateBpsSchema,
    maxRateBps: rateBpsSchema,
    defaultMinBoxPriceCents: minBoxPriceCentsSchema,
  })
  .refine((settings) => settings.defaultRateBps <= settings.maxRateBps, {
    message: "the default rate cannot be above the maximum rate",
    path: ["defaultRateBps"],
  });
export type LiveBreakSettingsInput = z.infer<typeof liveBreakSettingsInputSchema>;

/** The settings' defaults, as `liveBreakSettingsSchema` reads `{}`. */
export const LIVE_BREAK_SETTINGS_DEFAULTS: LiveBreakSettings = liveBreakSettingsSchema.parse({});

/** A host's own rate as the server reads it: their stored rate, else the default, never above the maximum. */
export function effectiveLiveBreakRateBps(
  ownRateBps: number | null,
  settings: Pick<LiveBreakSettings, "defaultRateBps" | "maxRateBps">,
): number {
  return Math.min(ownRateBps ?? settings.defaultRateBps, settings.maxRateBps);
}

// ---- refusal causes, carried in `details.reason` ----------------------------------------------

/** Why `live_break_not_accepting_purchases`: purchases stopped, the live break is Draining, or its host is away. */
export const LIVE_BREAK_NOT_ACCEPTING_REASONS = ["stopped", "draining", "host_away"] as const;
export type LiveBreakNotAcceptingReason = (typeof LIVE_BREAK_NOT_ACCEPTING_REASONS)[number];

/** Why `live_break_box_not_eligible`. */
export const LIVE_BREAK_BOX_INELIGIBLE_REASONS = [
  "not_openable",
  "excluded_category",
  "below_min_price",
  "not_in_live_break",
] as const;
export type LiveBreakBoxIneligibleReason = (typeof LIVE_BREAK_BOX_INELIGIBLE_REASONS)[number];

/** Which cap `live_break_queue_full` hit: the live break's, or the buyer's own. */
export const LIVE_BREAK_QUEUE_FULL_REASONS = ["live_break", "buyer"] as const;
export type LiveBreakQueueFullReason = (typeof LIVE_BREAK_QUEUE_FULL_REASONS)[number];

/** Why `live_break_entry_not_cancellable`. */
export const LIVE_BREAK_ENTRY_NOT_CANCELLABLE_REASONS = [
  "presenting",
  "up_next",
  "not_queued",
] as const;
export type LiveBreakEntryNotCancellableReason =
  (typeof LIVE_BREAK_ENTRY_NOT_CANCELLABLE_REASONS)[number];

/** Why `live_break_entry_not_pickable`. */
export const LIVE_BREAK_ENTRY_NOT_PICKABLE_REASONS = [
  "presenting",
  "not_queued",
  "draining",
] as const;
export type LiveBreakEntryNotPickableReason =
  (typeof LIVE_BREAK_ENTRY_NOT_PICKABLE_REASONS)[number];

/** Why `live_break_title_rejected`: too long or empty, a word the filter refuses, or a link. */
export const LIVE_BREAK_TITLE_REJECTED_REASONS = ["length", "prohibited", "link"] as const;
export type LiveBreakTitleRejectedReason = (typeof LIVE_BREAK_TITLE_REJECTED_REASONS)[number];

/**
 * Why `live_break_settings_invalid`: a rule across fields that the input's ranges cannot say.
 * `rate_above_max` carries `details.maxRateBps`.
 */
export const LIVE_BREAK_SETTINGS_INVALID_REASONS = [
  "pause_needs_auto_mode",
  "house_auto_mode",
  "house_commission",
  "rate_above_max",
  "end_date_house_only",
  "end_date_past",
  "box_count",
] as const;
export type LiveBreakSettingsInvalidReason = (typeof LIVE_BREAK_SETTINGS_INVALID_REASONS)[number];

/** Why `live_break_playlist_invalid`. */
export const LIVE_BREAK_PLAYLIST_INVALID_REASONS = [
  "name_taken",
  "name_invalid",
  "full",
  "count_limit",
  "order_mismatch",
] as const;
export type LiveBreakPlaylistInvalidReason = (typeof LIVE_BREAK_PLAYLIST_INVALID_REASONS)[number];

/** Why `live_break_not_announceable`: announced before, private, closed, or Draining. */
export const LIVE_BREAK_NOT_ANNOUNCEABLE_REASONS = [
  "announced",
  "private",
  "closed",
  "draining",
] as const;
export type LiveBreakNotAnnounceableReason = (typeof LIVE_BREAK_NOT_ANNOUNCEABLE_REASONS)[number];

// ---- the closed sets the tables and the wire share ---------------------------------------------

/** A live break's status: `closed` is terminal. Everything else is derived while it is open. */
export const LIVE_BREAK_STATUSES = ["open", "closed"] as const;
export type LiveBreakStatus = (typeof LIVE_BREAK_STATUSES)[number];

/** Which step of an Opening the stage shows: nothing, the Presentation, or the Reveal. */
export const LIVE_BREAK_REVEAL_PHASES = ["idle", "presenting", "revealed"] as const;
export type LiveBreakRevealPhase = (typeof LIVE_BREAK_REVEAL_PHASES)[number];

/**
 * Why a live break closed: its host ended it, staff ended it, its scheduled end passed, its host
 * left it idle and empty (`abandoned`), or staff force-closed it.
 */
export const LIVE_BREAK_CLOSE_REASONS = [
  "host_ended",
  "staff_ended",
  "end_date",
  "abandoned",
  "force_closed",
] as const;
export type LiveBreakCloseReason = (typeof LIVE_BREAK_CLOSE_REASONS)[number];

/** Who pressed End; a scheduled end (a house live break's `ends_at`) records none. */
export const LIVE_BREAK_ENDED_BY = ["host", "staff"] as const;
export type LiveBreakEndedBy = (typeof LIVE_BREAK_ENDED_BY)[number];

/**
 * A house live break's profile. `idle_end` (dev and personal stages only) ends itself once its
 * queue sits empty for `LIVE_BREAK_IDLE_END_MS` after an Opening, and the tick respawns it.
 */
export const LIVE_BREAK_HOUSE_PROFILES = ["idle_end"] as const;
export type LiveBreakHouseProfile = (typeof LIVE_BREAK_HOUSE_PROFILES)[number];

/** A queue entry's status: waiting, opened on stage, or released (its hold given back). */
export const LIVE_BREAK_ENTRY_STATUSES = ["queued", "opened", "released"] as const;
export type LiveBreakEntryStatus = (typeof LIVE_BREAK_ENTRY_STATUSES)[number];

/**
 * How an entry got its place: `next` for the host's Open Next and a Pick, `priority` for a buyer
 * subscribed to the live break's Twitch channel, `standard` for everyone else.
 */
export const LIVE_BREAK_QUEUE_TIERS = ["next", "priority", "standard"] as const;
export type LiveBreakQueueTier = (typeof LIVE_BREAK_QUEUE_TIERS)[number];

/** Why an entry left the queue unopened. */
export const LIVE_BREAK_RELEASE_REASONS = [
  "buyer_cancelled",
  "staff_cancelled",
  "blocked",
  "live_break_closed",
] as const;
export type LiveBreakReleaseReason = (typeof LIVE_BREAK_RELEASE_REASONS)[number];

/** Who blocked a shopper: the host, or tenant staff. */
export const LIVE_BREAK_BLOCKED_BY = ["host", "staff"] as const;
export type LiveBreakBlockedBy = (typeof LIVE_BREAK_BLOCKED_BY)[number];

/** Most playlists an environment keeps, and most boxes one holds. */
export const LIVE_BREAK_MAX_PLAYLISTS = 50;
export const LIVE_BREAK_MAX_PLAYLIST_BOXES = 50;
/** Longest playlist name, in characters. */
export const LIVE_BREAK_PLAYLIST_NAME_MAX = 40;
/** How many of the newest viewers the roster names. */
export const LIVE_BREAK_ROSTER_MAX = 500;
/** How many top pulls a live break's stats list. */
export const LIVE_BREAK_TOP_PULLS = 5;
/** How often a watching shopper's page sends its heartbeat. */
export const LIVE_BREAK_HEARTBEAT_INTERVAL_MS = 45_000;

// ---- Twitch: each environment's own app (docs/live-breaks.md, "Twitch") ------------------

/**
 * Why `twitch_not_enabled`: the environment has no Twitch app, the platform has no Twitch key, or
 * the app's saved secret was written under another key (a changed `TWITCH_TOKEN_KEY`) and the
 * tenant must save the app again.
 */
export const TWITCH_NOT_ENABLED_REASONS = ["no_app", "no_key", "undecryptable"] as const;
export type TwitchNotEnabledReason = (typeof TWITCH_NOT_ENABLED_REASONS)[number];

/** The one scope a shopper's link asks for: reading their own subscriptions, for priority. */
export const TWITCH_LINK_SCOPES = ["user:read:subscriptions"] as const;

/**
 * The store BFF's route that starts a shopper's Twitch link (`GET`, `?returnTo=` a same-origin
 * path): it redirects to Twitch's consent screen with the environment's own app.
 */
export const TWITCH_LINK_PATH = "/api/twitch/link";

/**
 * Where Twitch sends the shopper back, on the store's own origin. Each store hostname registers
 * `https://{hostname}` followed by this path as a redirect URI in the environment's Twitch app.
 */
export const TWITCH_CALLBACK_PATH = "/api/twitch/callback";

/**
 * The `type` of the message the callback page posts to the window that opened it (its own origin
 * only): `{ type, ok: true }` or `{ type, ok: false, error }`, `error` one of `TWITCH_LINK_ERRORS`.
 */
export const TWITCH_LINK_MESSAGE_TYPE = "loot-twitch-link";

/**
 * The query parameter the callback adds to `returnTo` when no window opened it (the popup was
 * blocked): `ok`, or one of `TWITCH_LINK_ERRORS`.
 */
export const TWITCH_LINK_RESULT_PARAM = "twitchLink";

/** Why a Twitch link did not complete, as the callback reports it. */
export const TWITCH_LINK_ERRORS = [
  /** The shopper declined on Twitch's consent screen. */
  "access_denied",
  /** Twitch answered the consent screen with some other error. */
  "twitch_error",
  /** The `state` did not match this browser's link cookie (expired, or another tab's). */
  "invalid_state",
  /** Twitch came back with no authorization code. */
  "missing_code",
  /** No shopper is signed in on this store. */
  "not_signed_in",
  /** Twitch is off on this store: no Twitch app, no platform Twitch key, or live breaks off. */
  "twitch_not_enabled",
  /** Twitch refused the code exchange. */
  "twitch_code_rejected",
  /** The Twitch account is already linked to another shopper of this store. */
  "twitch_already_linked",
  /** Twitch did not answer. */
  "twitch_unavailable",
  /** Anything else. */
  "unknown",
] as const;
export type TwitchLinkError = (typeof TWITCH_LINK_ERRORS)[number];
